// src/components/tasks/TaskCalendar.js
// ✅ DROP-IN Task calendar view (day/week/month) modeled after SampleOrderCalendar
// ✅ Uses TaskService.getCompanyTasks (rules-friendly visibility)
// ✅ Admin/Owner can drag + drop tasks to reschedule due date
// ✅ Overdue tasks show red (not completed)
// ✅ IMPROVED: Better task tile readability with clearer titles and layout

import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Sun,
  User,
  Briefcase,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Target,
  Download,
  RefreshCw,
  Plus,
} from "lucide-react";
import { TaskService } from "./TaskService";
import {
  downloadOutlookCalendarFile,
  getWeekRange,
  isDateWithinWeek,
} from "../../utils/outlookCalendarExport";

// Helper: format Date -> "YYYY-MM-DD"
const formatYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Parse ISO / Date / Firestore timestamp-ish into Date
const toDateFromRaw = (raw) => {
  if (!raw) return null;
  if (raw && typeof raw.toDate === "function") return raw.toDate();
  if (raw instanceof Date) return raw;
  if (typeof raw === "string") {
    const dt = new Date(raw);
    if (!Number.isNaN(dt.getTime())) return dt;
  }
  if (typeof raw === "number") {
    const dt = new Date(raw);
    if (!Number.isNaN(dt.getTime())) return dt;
  }
  return null;
};

const isWeekend = (date) => {
  const d = date.getDay();
  return d === 0 || d === 6;
};

const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getBaseRangeForView = (currentDate, viewMode) => {
  const base = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

  if (viewMode === "day") return { start: base, end: base };

  if (viewMode === "week") {
    const start = new Date(base);
    const day = start.getDay();
    start.setDate(start.getDate() - day);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start, end };
  }

  const start = new Date(base.getFullYear(), base.getMonth(), 1);
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  return { start, end };
};

const getAllDatesInRange = (start, end) => {
  const dates = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (cursor <= end) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const buildMonthGrid = (baseStart) => {
  const year = baseStart.getFullYear();
  const month = baseStart.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startDay = firstOfMonth.getDay();
  const gridStart = new Date(year, month, 1 - startDay);

  const weeks = [];
  let current = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate());

  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
};

function roleLower(role) {
  return (role || "").toString().trim().toLowerCase().replace(/[_\s-]+/g, "");
}

function isOwnerOrAdmin(me) {
  const r = roleLower(me?.role);
  return r === "owner" || r === "admin";
}

function getMyUid(me) {
  return me?.uid || me?.id || null;
}

function computeTaskEffectiveStatus(task, myUid) {
  // For joint tasks, if perUser exists, show "my status" (boss will still see overall status too)
  if (task?.distribution === "joint" && task?.perUser && myUid) {
    const mine = task.perUser?.[myUid]?.status;
    if (mine) return mine;
  }
  return task?.status || "To Do";
}

function isTaskFullyCompleted(task) {
  const status = task?.status || "To Do";
  if (status === "Completed") return true;

  // If joint: if all perUser statuses are Completed
  if (task?.distribution === "joint" && task?.perUser && Array.isArray(task?.memberIds)) {
    const allDone = task.memberIds.every((uid) => task.perUser?.[uid]?.status === "Completed");
    return allDone;
  }

  return false;
}

function getAssignedLabel(task, usersMap = {}, groupsMap = {}) {
  const assignMode = task?.assignMode || "users";

  if (assignMode === "group") {
    const gId = task?.groupId || null;
    const gName = task?.groupName || groupsMap?.[gId]?.name || "Group";
    if (task?.distribution === "soloCopies") return `${gName} (solo)`;
    return `${gName} (joint)`;
  }

  const ids = Array.isArray(task?.assignedToUserIds) ? task.assignedToUserIds : [];
  if (!ids.length && task?.distribution === "joint" && Array.isArray(task?.memberIds)) {
    const n = task.memberIds.length;
    return n ? `Joint (${n})` : "Unassigned";
  }

  if (ids.length === 1) {
    const u = usersMap?.[ids[0]];
    const name = u?.displayName || u?.fullName || u?.name || u?.email || "Assigned";
    return name;
  }

  if (ids.length > 1) return `Joint (${ids.length})`;
  return "Unassigned";
}

function getAccountName(task, accountsMap = {}) {
  if (!task?.accountId) return null;
  const a = accountsMap?.[task.accountId];
  return a?.name || a?.accountName || a?.title || null;
}

function getDueTimeLabel(dueDate) {
  if (!dueDate) return null;
  const hrs = dueDate.getHours();
  const mins = dueDate.getMinutes();
  if (hrs === 0 && mins === 0) return null;
  return dueDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// NEW: Get task priority label and color
function getTaskPriority(task) {
  const priority = (task?.priority || "").toString().toLowerCase();
  switch (priority) {
    case "high": return { label: "High", color: "bg-red-100 border-red-200 text-red-700", icon: "🔴" };
    case "medium": return { label: "Medium", color: "bg-amber-100 border-amber-200 text-amber-700", icon: "🟡" };
    case "low": return { label: "Low", color: "bg-blue-100 border-blue-200 text-blue-700", icon: "🔵" };
    default: return { label: "Normal", color: "bg-slate-100 border-slate-200 text-slate-700", icon: "⚪" };
  }
}

// NEW: Get task type icon and color
function getTaskType(task) {
  const type = (task?.type || "").toString().toLowerCase();
  switch (type) {
    case "call": return { icon: "📞", label: "Call", color: "bg-blue-50 text-blue-700" };
    case "meeting": return { icon: "👥", label: "Meeting", color: "bg-purple-50 text-purple-700" };
    case "email": return { icon: "✉️", label: "Email", color: "bg-green-50 text-green-700" };
    case "followup": return { icon: "🔄", label: "Follow-up", color: "bg-amber-50 text-amber-700" };
    case "sample": return { icon: "🎁", label: "Sample", color: "bg-pink-50 text-pink-700" };
    default: return { icon: "📝", label: "Task", color: "bg-slate-50 text-slate-700" };
  }
}

export default function TaskCalendar({
  companyId,
  me,
  usersMap = {},
  accountsMap = {},
  groupsMap = {},
  onEdit, // (task) => void
  onRefresh,
  refreshing = false,
  onCreateTask,
  refreshKey, // optional: changing this triggers reload
}) {
  const [viewMode, setViewMode] = useState("month"); // day | week | month
  const [excludeWeekends, setExcludeWeekends] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [draggedTaskId, setDraggedTaskId] = useState(null);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const myUid = getMyUid(me);
  const canDragDates = isOwnerOrAdmin(me);

  useEffect(() => {
    let alive = true;

    async function load() {
      if (!companyId) return;
      setLoading(true);
      setErr("");
      try {
        const list = await TaskService.getCompanyTasks(companyId, { me });
        if (!alive) return;
        setTasks(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("[TaskCalendar] load failed:", e);
        if (!alive) return;
        setErr(e?.message || "Failed to load tasks.");
        setTasks([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [companyId, me, refreshKey]);

  const nowYMD = formatYMD(new Date());
  const thisWeekRange = useMemo(() => getWeekRange(new Date()), []);

  const { start: baseStart, end: baseEnd } = useMemo(
    () => getBaseRangeForView(currentDate, viewMode),
    [currentDate, viewMode]
  );

  const weeks = useMemo(() => {
    if (viewMode === "month") return buildMonthGrid(baseStart);
    const dates = getAllDatesInRange(baseStart, baseEnd);
    return [dates];
  }, [viewMode, baseStart, baseEnd]);

  const headerLabels = useMemo(() => {
    if (viewMode === "day") return [baseStart.toLocaleDateString("en-US", { weekday: "short" })];

    if (viewMode === "week") {
      const weekDates = getAllDatesInRange(baseStart, baseEnd);
      const filtered = excludeWeekends ? weekDates.filter((d) => !isWeekend(d)) : weekDates;
      return filtered.map((d) => d.toLocaleDateString("en-US", { weekday: "short" }));
    }

    return excludeWeekends ? weekdayLabels.filter((_, i) => i > 0 && i < 6) : weekdayLabels;
  }, [viewMode, baseStart, baseEnd, excludeWeekends]);

  const rangeLabel = useMemo(() => {
    if (viewMode === "day") {
      return baseStart.toLocaleDateString("en-US", {
        weekday: "short",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
    if (viewMode === "week") {
      const startStr = baseStart.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const endStr = baseEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      return `${startStr} – ${endStr}`;
    }
    return baseStart.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }, [viewMode, baseStart, baseEnd]);

  const handlePrev = () => {
    const next = new Date(currentDate);
    if (viewMode === "day") next.setDate(next.getDate() - 1);
    else if (viewMode === "week") next.setDate(next.getDate() - 7);
    else next.setMonth(next.getMonth() - 1);
    setCurrentDate(next);
  };

  const handleNext = () => {
    const next = new Date(currentDate);
    if (viewMode === "day") next.setDate(next.getDate() + 1);
    else if (viewMode === "week") next.setDate(next.getDate() + 7);
    else next.setMonth(next.getMonth() + 1);
    setCurrentDate(next);
  };

  const handleToday = () => setCurrentDate(new Date());

  const gridColsClass = viewMode === "day" ? "grid-cols-1" : excludeWeekends ? "grid-cols-5" : "grid-cols-7";

  const tasksByDate = useMemo(() => {
    const map = {};
    const unscheduled = [];
    const overdue = [];

    (Array.isArray(tasks) ? tasks : []).forEach((t) => {
      const due = toDateFromRaw(t?.dueAt);
      const completed = isTaskFullyCompleted(t);

      if (!due) {
        unscheduled.push(t);
        return;
      }

      const ymd = formatYMD(due);
      if (!map[ymd]) map[ymd] = [];
      map[ymd].push(t);

      if (!completed && ymd < nowYMD) overdue.push(t);
    });

    // Sort within each day: overdue first, then time, then title
    const sortDay = (arr) => {
      return [...arr].sort((a, b) => {
        const ad = toDateFromRaw(a?.dueAt);
        const bd = toDateFromRaw(b?.dueAt);
        const aY = ad ? formatYMD(ad) : "";
        const bY = bd ? formatYMD(bd) : "";
        const aOver = !isTaskFullyCompleted(a) && aY && aY < nowYMD;
        const bOver = !isTaskFullyCompleted(b) && bY && bY < nowYMD;
        if (aOver !== bOver) return aOver ? -1 : 1;
        const aTime = ad ? ad.getTime() : 0;
        const bTime = bd ? bd.getTime() : 0;
        if (aTime !== bTime) return aTime - bTime;
        return String(a?.title || "").localeCompare(String(b?.title || ""));
      });
    };

    Object.keys(map).forEach((k) => (map[k] = sortDay(map[k])));

    return { map, unscheduled, overdue };
  }, [tasks, nowYMD]);

  const weeklyFollowUpEvents = useMemo(() => {
    return (Array.isArray(tasks) ? tasks : [])
      .filter((task) => {
        const type = String(task?.type || "").trim().toLowerCase();
        if (type !== "follow-up" && type !== "follow up") return false;
        if (isTaskFullyCompleted(task)) return false;
        const due = toDateFromRaw(task?.dueAt);
        return !!due && isDateWithinWeek(due, thisWeekRange);
      })
      .map((task) => {
        const due = toDateFromRaw(task?.dueAt);
        const end = new Date(due);
        end.setMinutes(end.getMinutes() + 30);
        const accountName = getAccountName(task, accountsMap) || "No Account";
        const descriptionParts = [
          `Task: ${task?.title || "Follow-up task"}`,
          `Account: ${accountName}`,
          task?.description ? `Notes: ${task.description}` : null,
          task?.status ? `Status: ${task.status}` : null,
        ].filter(Boolean);

        return {
          uid: `follow-up-task-${task.id}-${due.toISOString()}`,
          title: task?.title || `${accountName} follow-up`,
          start: due,
          end,
          allDay: false,
          description: descriptionParts.join("\n"),
          url: `${window.location.origin}/app/tasks?taskId=${encodeURIComponent(task.id)}`,
        };
      });
  }, [accountsMap, tasks, thisWeekRange]);

  const handleExportWeeklyFollowUps = () => {
    if (weeklyFollowUpEvents.length === 0) {
      window.alert("There are no follow-up tasks due this week.");
      return;
    }

    const weekLabel = formatYMD(thisWeekRange.start);
    downloadOutlookCalendarFile({
      filename: `supplyhawk-follow-up-tasks-${weekLabel}.ics`,
      events: weeklyFollowUpEvents,
    });
  };

  const scheduledCount = useMemo(() => {
    let c = 0;
    Object.values(tasksByDate.map).forEach((arr) => (c += arr.length));
    return c;
  }, [tasksByDate.map]);

  const overdueCount = useMemo(() => tasksByDate.overdue.length, [tasksByDate.overdue.length]);
  const unscheduledCount = useMemo(() => tasksByDate.unscheduled.length, [tasksByDate.unscheduled.length]);

  // Drag & drop: reschedule due date (admin/owner only)
  const handleDragStart = (e, taskId) => {
    if (!canDragDates) return;
    setDraggedTaskId(taskId);
    e.dataTransfer.setData("taskId", taskId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    if (!canDragDates) return;
    setDraggedTaskId(null);
  };

  const handleDayDragOver = (e) => {
    if (!canDragDates) return;
    e.preventDefault();
    e.currentTarget.classList.add("ring-2", "ring-blue-400");
  };

  const handleDayDragLeave = (e) => {
    if (!canDragDates) return;
    e.currentTarget.classList.remove("ring-2", "ring-blue-400");
  };

  const handleDayDrop = async (e, date) => {
    if (!canDragDates) return;
    e.preventDefault();
    e.currentTarget.classList.remove("ring-2", "ring-blue-400");

    const taskId = e.dataTransfer.getData("taskId");
    setDraggedTaskId(null);

    if (!taskId) return;

    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    // Keep time if present; otherwise default 09:00
    const currentDue = toDateFromRaw(task?.dueAt);
    const timeLabel = currentDue ? `${String(currentDue.getHours()).padStart(2, "0")}:${String(currentDue.getMinutes()).padStart(2, "0")}` : "09:00";

    const newDueDate = formatYMD(date);

    try {
      await TaskService.updateTask(companyId, taskId, {
        dueDate: newDueDate,
        dueTime: timeLabel,
        updatedByUid: myUid || undefined,
        updatedByRole: me?.role || undefined,
      });

      // optimistic update
      setTasks((prev) =>
        prev.map((t) => {
          if (t.id !== taskId) return t;
          const dt = new Date(`${newDueDate}T${timeLabel}`);
          return { ...t, dueAt: dt.toISOString() };
        })
      );
    } catch (err2) {
      console.error("[TaskCalendar] reschedule failed:", err2);
      setErr(err2?.message || "Failed to reschedule task.");
    }
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="p-4 md:p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Tasks Calendar</h2>
              <p className="text-xs text-slate-500">
                {scheduledCount} scheduled • {overdueCount} overdue • {unscheduledCount} unscheduled
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {onRefresh ? (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {refreshing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Refresh
              </button>
            ) : null}

            {onCreateTask ? (
              <button
                type="button"
                onClick={onCreateTask}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
              >
                <Plus className="h-3.5 w-3.5" />
                New Task
              </button>
            ) : null}

            <button
              type="button"
              onClick={handleExportWeeklyFollowUps}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <Download className="h-3.5 w-3.5" />
              Add this week's Follow Up Tasks to Outlook
            </button>

            {/* View toggles */}
            <div className="flex bg-slate-100 rounded-lg p-1 text-xs font-medium">
              <button
                type="button"
                onClick={() => setViewMode("day")}
                className={`px-3 py-1.5 rounded-md ${
                  viewMode === "day" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Day
              </button>
              <button
                type="button"
                onClick={() => setViewMode("week")}
                className={`px-3 py-1.5 rounded-md ${
                  viewMode === "week" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Week
              </button>
              <button
                type="button"
                onClick={() => setViewMode("month")}
                className={`px-3 py-1.5 rounded-md ${
                  viewMode === "month" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Month
              </button>
            </div>

            {/* Exclude weekends toggle */}
            <button
              type="button"
              onClick={() => setExcludeWeekends((prev) => !prev)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium ${
                excludeWeekends
                  ? "bg-amber-50 border-amber-200 text-amber-800"
                  : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Sun className="w-3.5 h-3.5" />
              {excludeWeekends ? "Weekdays only" : "Include weekends"}
            </button>

            {/* Navigation */}
            <div className="flex items-center gap-1 border border-slate-200 rounded-lg px-1 py-0.5 text-xs">
              <button type="button" onClick={handlePrev} className="p-1 rounded-md hover:bg-slate-100">
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              </button>
              <span className="px-2 font-medium text-slate-700 whitespace-nowrap">{rangeLabel}</span>
              <button type="button" onClick={handleNext} className="p-1 rounded-md hover:bg-slate-100">
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
              <button
                type="button"
                onClick={handleToday}
                className="ml-1 px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                Today
              </button>
            </div>
          </div>
        </div>

        {/* Status row */}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
          {canDragDates ? (
            <div className="px-2 py-1 rounded-full border border-blue-200 bg-blue-50 text-blue-700 inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Drag & drop to reschedule
            </div>
          ) : null}
        </div>

        {err ? (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">
            {err}
          </div>
        ) : null}

        {loading ? (
          <div className="mt-3 text-xs text-slate-600">Loading tasks…</div>
        ) : null}
      </div>

      {/* Calendar grid */}
      <div className="px-4 pb-4 md:px-5 md:pb-5">
        <div className="rounded-xl border border-slate-200 overflow-hidden max-h-[70vh] flex flex-col">
          {/* Weekday header row */}
          <div className={`grid ${gridColsClass} bg-slate-50 border-b border-slate-200 text-[11px] font-medium text-slate-500`}>
            {headerLabels.map((label) => (
              <div key={label} className="px-2 py-1.5 text-center uppercase tracking-wide">
                {label}
              </div>
            ))}
          </div>

          {/* Weeks / rows */}
          <div className="flex-1 overflow-y-auto">
            {weeks.map((week, weekIndex) => {
              const daysInRow = excludeWeekends ? week.filter((d) => !isWeekend(d)) : week;

              return (
                <div
                  key={weekIndex}
                  className={`grid ${gridColsClass} min-h-[110px] border-b border-slate-200 last:border-b-0`}
                >
                  {daysInRow.map((date) => {
                    const ymd = formatYMD(date);
                    const isToday = nowYMD === ymd;
                    const inCurrentMonth = viewMode !== "month" || date.getMonth() === baseStart.getMonth();
                    const dayTasks = tasksByDate.map[ymd] || [];

                    return (
                      <div
                        key={ymd}
                        className={`relative border-r border-slate-200 last:border-r-0 bg-white transition-all ${
                          !inCurrentMonth && viewMode === "month" ? "bg-slate-50 text-slate-400" : ""
                        }`}
                        onDragOver={handleDayDragOver}
                        onDragLeave={handleDayDragLeave}
                        onDrop={(e) => handleDayDrop(e, date)}
                      >
                        {/* Day header */}
                        <div className="flex items-center justify-between px-2 pt-1 pb-0.5 text-[11px]">
                          <div className="flex items-center gap-1">
                            <span className={`font-semibold ${!inCurrentMonth && viewMode === "month" ? "text-slate-400" : "text-slate-700"}`}>
                              {date.getDate()}
                            </span>
                            {isToday ? (
                              <span className="text-[9px] px-1 py-0.5 rounded-full bg-blue-600 text-white">Today</span>
                            ) : null}
                          </div>

                          {dayTasks.length > 0 ? (
                            <span className="text-[10px] text-slate-400">
                              {dayTasks.length} {dayTasks.length === 1 ? "task" : "tasks"}
                            </span>
                          ) : null}
                        </div>

                        {/* Tasks */}
                        <div className="px-1.5 pb-2 pt-1 space-y-1.5">
                          {dayTasks.length === 0 && canDragDates ? (
                            <div className="h-7 text-[10px] text-slate-300 flex items-center justify-center border border-dashed border-slate-200 rounded-md">
                              Drop here
                            </div>
                          ) : null}

                          {dayTasks.map((task) => {
                            const due = toDateFromRaw(task?.dueAt);
                            const dueTime = getDueTimeLabel(due);

                            const completed = isTaskFullyCompleted(task);
                            const overdue = !completed && ymd < nowYMD;

                            const assignedLabel = getAssignedLabel(task, usersMap, groupsMap);
                            const accountName = getAccountName(task, accountsMap);

                            const myStatus = computeTaskEffectiveStatus(task, myUid);
                            const isDragged = draggedTaskId === task.id;

                            const statusLabel = completed ? "Completed" : (task?.status || "To Do");
                            
                            // NEW: Get priority and type info
                            const priority = getTaskPriority(task);
                            const taskType = getTaskType(task);

                            const tooltipParts = [
                              task?.title ? `Title: ${task.title}` : null,
                              accountName ? `Account: ${accountName}` : null,
                              assignedLabel ? `Assigned: ${assignedLabel}` : null,
                              dueTime ? `Time: ${dueTime}` : null,
                              `Status: ${statusLabel}`,
                              `Priority: ${priority.label}`,
                              `Type: ${taskType.label}`,
                              myUid && task?.distribution === "joint" ? `My status: ${myStatus}` : null,
                            ].filter(Boolean);

                            return (
                              <div
                                key={task.id}
                                draggable={canDragDates}
                                onDragStart={(e) => handleDragStart(e, task.id)}
                                onDragEnd={handleDragEnd}
                                onClick={() => onEdit && onEdit(task)}
                                title={tooltipParts.join("\n")}
                                className={[
                                  "group rounded-md border px-2 py-1.5 text-[11px] leading-tight transition-colors",
                                  canDragDates ? "cursor-move" : "cursor-pointer",
                                  isDragged ? "opacity-60 ring-1 ring-blue-400" : "",
                                  overdue
                                    ? "bg-red-50 border-red-200 hover:bg-red-100"
                                    : completed
                                    ? "bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
                                    : "bg-sky-50 border-sky-100 hover:bg-sky-100 hover:border-sky-200",
                                ].join(" ")}
                              >
                                {/* NEW: Top row with priority and type */}
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-1">
                                    <span className="text-[10px]">{taskType.icon}</span>
                                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${priority.color} font-medium`}>
                                      {priority.label}
                                    </span>
                                  </div>
                                  {dueTime ? (
                                    <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-white/70 border border-slate-200 text-slate-700">
                                      <Clock className="w-3 h-3" />
                                      {dueTime}
                                    </span>
                                  ) : null}
                                </div>

                                {/* Task title - IMPROVED readability */}
                                <div className="mb-1.5">
                                  <div className="font-bold text-[12px] text-slate-900 line-clamp-2 leading-tight min-h-[2.4em]">
                                    {task.title || "Untitled Task"}
                                  </div>
                                  {accountName ? (
                                    <div className="flex items-center gap-1 text-[10px] text-slate-600 mt-0.5 truncate">
                                      <Briefcase className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                      <span className="truncate font-medium">{accountName}</span>
                                    </div>
                                  ) : null}
                                </div>

                                {/* Bottom row with assignment and status */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1 text-[10px] text-slate-600 truncate">
                                    <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                    <span className="truncate max-w-[100px] font-medium">{assignedLabel}</span>
                                  </div>

                                  <div className="flex items-center gap-1">
                                    {myUid && task?.distribution === "joint" ? (
                                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/60 border border-slate-200 text-slate-700 font-medium">
                                        {myStatus}
                                      </span>
                                    ) : null}
                                    <span
                                      className={[
                                        "inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border font-medium",
                                        overdue
                                          ? "bg-red-100 border-red-200 text-red-700"
                                          : completed
                                          ? "bg-emerald-100 border-emerald-200 text-emerald-700"
                                          : "bg-slate-100 border-slate-200 text-slate-700",
                                      ].join(" ")}
                                    >
                                      {overdue ? <AlertTriangle className="w-3 h-3" /> : completed ? <CheckCircle2 className="w-3 h-3" /> : <Target className="w-3 h-3" />}
                                      {overdue ? "Overdue" : completed ? "Done" : (task.status || "To Do")}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}
